import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { useHandleError, useQuery } from '@/hooks';
import {
	ErrorDisplay,
	MultiSelect,
	MultiSelectOption,
	flattenParentAndChildIdsForSelection,
	getTopLevelParentAndChildrenIds,
} from '@/components';
import {
	SORT_DIRECTION,
	TableCell,
	TableFilterDropdown,
	TableHeader,
	TableRenderer,
	TableRow,
} from '@/components/table';
import { USE_CASES_TABLE_COLUMN_IDS, useCasesTableColumns, useCasesTableHeaders } from '@/lib/__mocks__';
import { constructUrl, formOptionToMultiSelectOption, isErrorConfig } from '@/lib/utils';
import { TableColumns, TableRowConfig } from '@/components/table/models';
import { TABLE_TYPE_IDS, UseCase, UseCaseFiltersModel } from '@/lib/models';
import { useCaseService } from '@/lib/services';
import { getMergedTableColumns, storeTableColumnPreferences } from '@/lib/utils/table-utils';
import { ErrorConfig } from '@/lib/http-client';

export const UseCases = () => {
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const handleError = useHandleError();

	const query = useQuery();
	const programId = useMemo(() => query.get('programId') ?? '', [query]);
	const parentBusinessProcessIds = useMemo(() => query.get('parentBusinessProcessIds'), [query]);
	const businessProcessIds = useMemo(() => query.get('businessProcessIds'), [query]);
	const industryIds = useMemo(() => query.get('industryIds') ?? '', [query]);
	const aiToolIds = useMemo(() => query.get('aiToolIds') ?? '', [query]);
	const valueRatingIds = useMemo(() => query.get('valueRatingIds') ?? '', [query]);
	const page = useMemo(() => query.get('page') ?? '0', [query]);
	const size = useMemo(() => query.get('size') ?? '10', [query]);
	const sortDirection = useMemo(() => query.get('sortDirection'), [query]);
	const sortField = useMemo(() => query.get('sortField'), [query]);

	const [filtersAreLoading, setFiltersAreLoading] = useState(false);
	const [tableFilters, setTableFilters] = useState<UseCaseFiltersModel>();
	const [businessProcessOptions, setBusinessProcessOptions] = useState<MultiSelectOption[]>([]);

	const [tableIsLoading, setTableIsLoading] = useState(false);
	const [totalCount, setTotalCount] = useState(0);
	const [totalCountDescription, setTotalCountDescription] = useState('');
	const [tableColumnsData, setTableColumnsData] = useState(
		getMergedTableColumns(TABLE_TYPE_IDS.USE_CASES, useCasesTableColumns)
	);
	const [tableHeadersData] = useState(useCasesTableHeaders);
	const clearSelectionsRef = useRef<() => void>();
	const [tableRowData, setTableRowData] = useState<TableRowConfig<UseCase>[]>([]);
	const [pageError, setPageError] = useState<ErrorConfig | unknown>(undefined);

	/* ------------------------------------- */
	/* Must default a programId */
	/* ------------------------------------- */
	useEffect(() => {
		if (!tableFilters || tableFilters.programs.length <= 0) {
			return;
		}

		if (!programId) {
			navigate(constructUrl(pathname, { page: '0', programId: tableFilters.programs[0].id }, search), {
				replace: true,
			});
		}
	}, [navigate, pathname, programId, search, tableFilters]);

	const handlePageLoadError = useCallback((error: ErrorConfig | unknown) => {
		if (isErrorConfig(error) && error.status === 403) {
			setPageError(error);
			return true;
		}

		return false;
	}, []);

	const fetchFilters = useCallback(async () => {
		try {
			setFiltersAreLoading(true);

			const { programs, businessProcesses, industries, aiTools, valueRatings } = await useCaseService
				.getUseCaseFilters({
					...(programId && { programId }),
					...(parentBusinessProcessIds && { parentBusinessProcessIds }),
					...(businessProcessIds && { businessProcessIds }),
					...(industryIds && { industryIds }),
					...(aiToolIds && { aiToolIds }),
					...(valueRatingIds && { valueRatingIds }),
				})
				.fetch();

			setBusinessProcessOptions(formOptionToMultiSelectOption(businessProcesses));
			setTableFilters({
				programs,
				businessProcesses,
				industries,
				aiTools,
				valueRatings,
			});
		} catch (error) {
			if (!handlePageLoadError(error)) {
				handleError(error);
			}
		} finally {
			setFiltersAreLoading(false);
		}
	}, [
		aiToolIds,
		businessProcessIds,
		handleError,
		handlePageLoadError,
		industryIds,
		parentBusinessProcessIds,
		programId,
		valueRatingIds,
	]);

	useEffect(() => {
		fetchFilters();
	}, [fetchFilters]);

	const fetchData = useCallback(async () => {
		if (!programId) {
			return;
		}
		try {
			setTableIsLoading(true);

			const response = await useCaseService
				.getUseCases({
					...(page && { page }),
					...(size && { size }),
					...(sortField && { sortField }),
					...(sortDirection && { sortDirection }),
					...(programId && { programId }),
					...(parentBusinessProcessIds && { parentBusinessProcessIds }),
					...(businessProcessIds && { businessProcessIds }),
					...(industryIds && { industryIds }),
					...(aiToolIds && { aiToolIds }),
					...(valueRatingIds && { valueRatingIds }),
				})
				.fetch();

			const formattedUseCases = response.useCases.map((uc) => {
				return {
					rowId: uc.useCaseId,
					checked: false,
					expanded: false,
					columnData: uc,
				};
			});

			setTotalCount(response.totalCount);
			setTotalCountDescription(response.totalCountDescription);
			setTableRowData(formattedUseCases);
		} catch (error) {
			if (!handlePageLoadError(error)) {
				handleError(error);
			}
		} finally {
			setTableIsLoading(false);
		}
	}, [
		aiToolIds,
		businessProcessIds,
		handleError,
		handlePageLoadError,
		industryIds,
		page,
		parentBusinessProcessIds,
		programId,
		size,
		sortDirection,
		sortField,
		valueRatingIds,
	]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const allBpSelections = useMemo(() => {
		return flattenParentAndChildIdsForSelection(
			businessProcessOptions,
			parentBusinessProcessIds?.split('|').filter((s) => s),
			businessProcessIds?.split('|').filter((s) => s)
		);
	}, [businessProcessIds, businessProcessOptions, parentBusinessProcessIds]);

	const updateFilterSelection = useCallback(
		(filterKey: string, filterValue: string) => {
			if (clearSelectionsRef.current) {
				clearSelectionsRef.current();
			}

			navigate(
				constructUrl(
					pathname,
					{ page: '0', [filterKey]: filterValue },
					filterKey === 'programId' ? '' : search
				),
				{ replace: true }
			);
		},
		[navigate, pathname, search]
	);

	const handleBusinessProcessesChange = useCallback(
		(selectedBusinessProcessIds: string[]) => {
			if (clearSelectionsRef.current) {
				clearSelectionsRef.current();
			}

			const { parentIds, remainingChildrenIds } = getTopLevelParentAndChildrenIds(
				businessProcessOptions,
				selectedBusinessProcessIds
			);

			navigate(
				constructUrl(
					pathname,
					{
						page: '0',
						parentBusinessProcessIds: parentIds,
						businessProcessIds: remainingChildrenIds,
					},
					search
				),
				{ replace: true }
			);
		},
		[businessProcessOptions, navigate, pathname, search]
	);

	const handleTableColumnChange = useCallback((newColumns: TableColumns<USE_CASES_TABLE_COLUMN_IDS>) => {
		setTableColumnsData(newColumns);
		storeTableColumnPreferences(TABLE_TYPE_IDS.USE_CASES, newColumns);
	}, []);

	const handleTableSort = useCallback(
		(sortKey: string, sortDirection: SORT_DIRECTION) => {
			clearSelectionsRef.current?.();

			navigate(constructUrl(pathname, { page: '0', sortField: sortKey, sortDirection }, search), {
				replace: true,
			});
		},
		[navigate, pathname, search]
	);

	const handlePaginationChange = useCallback(
		({ pageSize, pageIndex }: { pageSize: number; pageIndex: number }) => {
			navigate(constructUrl(pathname, { page: String(pageIndex), size: String(pageSize) }, search), {
				replace: true,
			});
		},
		[navigate, pathname, search]
	);

	if (pageError) {
		return (
			<>
				<Helmet>
					<title>Hackett Connect - AI Use Cases</title>
				</Helmet>
				<ErrorDisplay error={pageError} showBackButton={true} />
			</>
		);
	}

	return (
		<>
			<Helmet>
				<title>Hackett Connect - AI Use Cases</title>
			</Helmet>

			<Container className="pt-7">
				<Row>
					<Col>
						<div className="d-flex align-items-center justify-content-between">
							<h1 className="mb-0 text-primary">AI Use Cases</h1>
						</div>
					</Col>
				</Row>
			</Container>
			<Container className="py-7">
				<Row className="mb-4">
					<Col lg={6}>
						<TableFilterDropdown
							filterLabel="Program"
							filterKey="programId"
							tableFilters={tableFilters?.programs ?? []}
							selectedFilters={{
								businessProcessIds: businessProcessIds ?? '',
								industryIds: industryIds ?? undefined,
								aiToolIds: aiToolIds ?? undefined,
								valueRatingIds: valueRatingIds ?? undefined,
								programId: programId ?? undefined,
							}}
							updateFilterSelection={updateFilterSelection}
							allowAny={false}
							disabled={filtersAreLoading}
						/>
					</Col>
				</Row>
				<Row>
					<Col lg={3} className="mb-4">
						<Form.Group>
							<Form.Label>Business Process</Form.Label>
							<MultiSelect
								options={businessProcessOptions}
								selected={allBpSelections}
								onChange={handleBusinessProcessesChange}
							>
								{businessProcessIds ? 'Select' : 'Any'}
							</MultiSelect>
						</Form.Group>
					</Col>
					<Col lg={3} className="mb-4">
						<TableFilterDropdown
							filterLabel="Industry"
							filterKey="industryIds"
							tableFilters={tableFilters?.industries ?? []}
							selectedFilters={{
								businessProcessIds: businessProcessIds ?? '',
								industryIds: industryIds ?? undefined,
								aiToolIds: aiToolIds ?? undefined,
								valueRatingIds: valueRatingIds ?? undefined,
								programId: programId ?? undefined,
							}}
							updateFilterSelection={updateFilterSelection}
							allowAny={true}
							disabled={filtersAreLoading}
						/>
					</Col>
					<Col lg={3} className="mb-4">
						<TableFilterDropdown
							filterLabel="AI Technology"
							filterKey="aiToolIds"
							tableFilters={tableFilters?.aiTools ?? []}
							selectedFilters={{
								businessProcessIds: businessProcessIds ?? '',
								industryIds: industryIds ?? undefined,
								aiToolIds: aiToolIds ?? undefined,
								valueRatingIds: valueRatingIds ?? undefined,
								programId: programId ?? undefined,
							}}
							updateFilterSelection={updateFilterSelection}
							allowAny={true}
							disabled={filtersAreLoading}
						/>
					</Col>
					<Col lg={3} className="mb-4">
						<TableFilterDropdown
							filterLabel="Value Rating"
							filterKey="valueRatingIds"
							tableFilters={tableFilters?.valueRatings ?? []}
							selectedFilters={{
								businessProcessIds: businessProcessIds ?? '',
								industryIds: industryIds ?? undefined,
								aiToolIds: aiToolIds ?? undefined,
								valueRatingIds: valueRatingIds ?? undefined,
								programId: programId ?? undefined,
							}}
							updateFilterSelection={updateFilterSelection}
							allowAny={true}
							disabled={filtersAreLoading}
						/>
					</Col>
				</Row>
				<Row className="mb-5">
					<Col>
						<TableRenderer
							isLoading={tableIsLoading}
							tableColumnData={tableColumnsData}
							onTableColumnDataChange={handleTableColumnChange}
							tableRowData={tableRowData}
							onTableRowDataChange={setTableRowData}
							tableHeaderRowRenderer={(_checkboxProps, clearSelections) => {
								clearSelectionsRef.current = clearSelections;

								return (
									<TableRow>
										{Object.values(tableColumnsData).map((columnConfig) => {
											if (!columnConfig.isShowing) {
												return null;
											}

											const tableHeader = tableHeadersData[columnConfig.columnId];
											const tableHeaderProps = {
												key: tableHeader.tableHeaderId,
												sortable: tableHeader.isSortable,
												sortKey: tableHeader.tableHeaderId,
												onSort: handleTableSort,
												sortDirection:
													sortField && sortField === tableHeader.tableHeaderId
														? (sortDirection as SORT_DIRECTION)
														: undefined,
											};

											switch (columnConfig.columnId) {
												case USE_CASES_TABLE_COLUMN_IDS.USE_CASES:
													return (
														<TableHeader {...tableHeaderProps}>
															{totalCountDescription} {tableHeader.title}
														</TableHeader>
													);
												default:
													return (
														<TableHeader {...tableHeaderProps}>
															{tableHeader.title}
														</TableHeader>
													);
											}
										})}
									</TableRow>
								);
							}}
							tableBodyRowRenderer={(data, _checkboxProps) => {
								return (
									<TableRow
										key={data.rowId}
										onClick={() => {
											navigate(`/use-cases/${data.rowId}`);
										}}
									>
										{Object.values(tableColumnsData).map((columnConfig) => {
											if (!columnConfig.isShowing) {
												return null;
											}

											const { columnData } = data;

											switch (columnConfig.columnId) {
												case USE_CASES_TABLE_COLUMN_IDS.USE_CASES:
													return (
														<TableCell key={columnConfig.columnId}>
															{columnData.name}
														</TableCell>
													);
												case USE_CASES_TABLE_COLUMN_IDS.NUMBER:
													return (
														<TableCell key={columnConfig.columnId}>
															{columnData.useCaseSequence}
														</TableCell>
													);

												case USE_CASES_TABLE_COLUMN_IDS.FUNCTION:
													return (
														<TableCell key={columnConfig.columnId}>
															{columnData.functionDescription}
														</TableCell>
													);
												case USE_CASES_TABLE_COLUMN_IDS.BUSINESS_PROCESS:
													return (
														<TableCell key={columnConfig.columnId}>
															{columnData.businessProcessesDescription}
														</TableCell>
													);
												case USE_CASES_TABLE_COLUMN_IDS.AI_TECHNOLOGY:
													return (
														<TableCell key={columnConfig.columnId} width={406}>
															{columnData.aiToolsDescription}
														</TableCell>
													);
												case USE_CASES_TABLE_COLUMN_IDS.VALUE_RATING:
													return (
														<TableCell key={columnConfig.columnId}>
															{columnData.valueRating.name}
														</TableCell>
													);
												default:
													return <TableCell key={columnConfig.columnId}>N/A</TableCell>;
											}
										})}
									</TableRow>
								);
							}}
							pageSize={parseInt(size, 10)}
							pageIndex={parseInt(page, 10)}
							tableRowDataTotal={totalCount}
							onPaginationChange={handlePaginationChange}
						/>
					</Col>
				</Row>
			</Container>
		</>
	);
};
